import { useState, useEffect, useRef } from 'react';
import { iqQuestions, iqCategoryMeta } from '@/mocks/iqTestData';
import type { IqCategory } from '@/mocks/iqTestData';
import IqVisualRenderer from './IqVisualRenderer';

interface Props {
  current: number;
  answers: Record<number, number>;
  onSelect: (answerIndex: number) => void;
  onNext: () => void;
  onPrev: () => void;
  onSubmit: () => void;
}

const categoryColorMap: Record<IqCategory, { badge: string; bar: string }> = {
  'shape-pattern': { badge: 'bg-primary-100 text-primary-700', bar: 'bg-primary-500' },
  number: { badge: 'bg-accent-100 text-accent-700', bar: 'bg-accent-500' },
  verbal: { badge: 'bg-secondary-100 text-secondary-700', bar: 'bg-secondary-500' },
  'overlap-2d': { badge: 'bg-primary-50 text-primary-700', bar: 'bg-primary-400' },
  'shape-rule': { badge: 'bg-accent-50 text-accent-700', bar: 'bg-accent-400' },
  'solid-3d': { badge: 'bg-secondary-50 text-secondary-700', bar: 'bg-secondary-400' },
};

const SHAPE_CHARS = ['○','●','△','▲','▽','▷','◁','□','■','◇','◆','◈','◉','⊙','◐','◑','⬠','⬡'];
const ARROW_CHARS = ['↑','→','↓','←','↗','↘','↙','↖'];
const LINE_CHARS = ['│','─','┌','┐','└','┘','├','┤','┬','┴','┼','+','×','╱','╲','✕'];

function renderOptionLabel(opt: string) {
  const first = opt.charAt(0);
  const isShape = (SHAPE_CHARS.includes(first) || ARROW_CHARS.includes(first) || LINE_CHARS.includes(first)) &&
    (opt.length === 1 || opt.split('').every((c) => c === first));

  if (!isShape) return <span className="text-sm leading-snug">{opt}</span>;

  const size =
    opt.length >= 4 ? 'text-xl' :
    opt.length === 3 ? 'text-2xl' :
    opt.length === 2 ? 'text-3xl' : 'text-4xl';

  return <span className={`${size} leading-none tracking-tighter whitespace-nowrap`}>{opt}</span>;
}

export default function IqQuestion({ current, answers, onSelect, onNext, onPrev, onSubmit }: Props) {
  const [selected, setSelected] = useState<number | null>(answers[iqQuestions[current].id] ?? null);
  const advancingRef = useRef(false);

  useEffect(() => {
    setSelected(answers[iqQuestions[current].id] ?? null);
  }, [current, answers]);

  const q = iqQuestions[current];
  const total = iqQuestions.length;
  const progress = (current / total) * 100;
  const catMeta = iqCategoryMeta.find((c) => c.id === q.category);
  const isLast = current + 1 === total;
  const hasVisualOptions = !!q.optionVisuals && q.optionVisuals.length > 0;

  const handleSelect = (idx: number) => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    setSelected(idx);
    onSelect(idx);
    setTimeout(() => {
      advancingRef.current = false;
      if (isLast) {
        onSubmit();
      } else {
        onNext();
      }
    }, 200);
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColorMap[q.category].badge}`}>
            {catMeta?.label}
          </span>
          <span className="text-sm text-foreground-500">{current + 1} / {total}</span>
        </div>
        <div className="w-full bg-background-200 rounded-full h-2">
          <div
            className={`${categoryColorMap[q.category].bar} h-2 rounded-full transition-all duration-300`}
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8 mb-4">
        <div className="flex items-start gap-3 mb-6">
          <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg flex-shrink-0 mt-0.5">
            <span className="text-white text-xs font-bold">{current + 1}</span>
          </div>
          <div className="flex-1">
            <IqVisualRenderer visualType={q.visualType} visualData={q.visualData} />
          </div>
        </div>

        {hasVisualOptions ? (
          <div className={`grid gap-3 ${q.options.length > 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2'}`}>
            {q.options.map((opt, idx) => {
              const vd = q.optionVisuals![idx];
              const isCompact = q.options.length > 4;
              return (
                <button
                  key={idx}
                  onClick={() => handleSelect(idx)}
                  className={`w-full rounded-xl border transition-all cursor-pointer ${isCompact ? 'p-2' : 'p-3'} ${
                    selected === idx
                      ? 'border-primary-400 bg-primary-50'
                      : 'border-background-200 bg-background-50 hover:border-background-300 hover:bg-background-100'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-bold ${selected === idx ? 'text-primary-700' : 'text-foreground-400'}`}>
                      {opt}
                    </span>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selected === idx ? 'border-primary-500 bg-primary-500' : 'border-background-300'
                    }`}>
                      {selected === idx && <i className="ri-check-line text-white text-xs"></i>}
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <IqVisualRenderer visualType={q.optionVisualType} visualData={vd} compact />
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className={`space-y-3 ${q.options.length > 4 ? 'md:grid md:grid-cols-2 md:space-y-0 md:gap-3' : ''}`}>
            {q.options.map((opt, idx) => (
              <button
                key={idx}
                onClick={() => handleSelect(idx)}
                className={`whitespace-normal w-full text-left px-4 py-3.5 rounded-xl border text-sm transition-all cursor-pointer ${
                  selected === idx
                    ? 'border-primary-400 bg-primary-50 text-primary-800 font-medium'
                    : 'border-background-200 bg-background-50 text-foreground-700 hover:border-background-300 hover:bg-background-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selected === idx ? 'border-primary-500 bg-primary-500' : 'border-background-300'
                  }`}>
                    {selected === idx && <i className="ri-check-line text-white text-xs"></i>}
                  </div>
                  {renderOptionLabel(opt)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-3">
        {current > 0 && (
          <button
            onClick={onPrev}
            className="whitespace-nowrap px-6 py-3 rounded-xl border border-background-300 text-foreground-600 font-semibold text-sm hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>이전
          </button>
        )}
        <span className="text-xs text-foreground-400 ml-auto">답안을 선택하면 자동으로 다음으로 넘어갑니다</span>
      </div>
    </div>
  );
}